// client/src/components/reports/ReportsPage.jsx
//
// /:tenant/reports — system templates, saved reports, recent exports and (for
// users with "manage") the role permissions. ?run=<id> (the link in a
// "report ready" push / email) highlights that export.
//
// API: /api/reports (server/routes/reportRoutes.js); tenant from the session.

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome, FaLock, FaPlus } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import ReportPermissions from './ReportPermissions';
import {
  listReports, listRuns, getRun, getReportPermissions, runDownloadUrl, errorText,
} from '../../services/reportsApi';
import '../analytics/PerformancePanel.css';
import '../forms/Forms.css';
import './Reports.css';

function when(value) {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T') + (String(value).includes('T') ? '' : 'Z'));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

export default function ReportsPage({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const tenant = Number(tenantProp || params.tenant);
  const highlightRun = Number(search.get('run')) || null;

  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [list, p] = await Promise.all([listReports(), getReportPermissions()]);
      setData(list);
      setConfig(p.config || null);
      if (!list.migrationPending) {
        const r = await listRuns().catch(() => ({ runs: [] }));
        let all = r.runs || [];
        // A run opened from a notification may not be one of "my" runs (scheduled).
        if (highlightRun && !all.some((x) => x.id === highlightRun)) {
          const one = await getRun(highlightRun).catch(() => null);
          if (one && one.run) all = [one.run, ...all];
        }
        setRuns(all);
      }
    } catch (err) {
      setError(errorText(err, t('reports.loadError', 'Could not load reports.')));
    }
  }, [highlightRun, t]);

  useEffect(() => { load(); }, [load]);

  const perms = data && data.permissions;
  const noAccess = perms && !perms.create && !perms.view_shared && !perms.export && data.reports.length === 0;
  const statusLabel = {
    queued: t('reports.runs.queued', 'Queued'),
    running: t('reports.runs.running', 'Running'),
    done: t('reports.runs.done', 'Ready'),
    failed: t('reports.runs.failed', 'Failed'),
    expired: t('reports.runs.expired', 'Expired'),
  };

  return (
    <div className="rp-page">
      <section className="surface">
        <div className="rp-head">
          <h2>{t('reports.title', 'Reports')}</h2>
          {perms && perms.create && !data.migrationPending && (
            <Link className="lm-btn" to={`/${tenant}/reports/new`}><FaPlus aria-hidden="true" /> {t('reports.new', 'New report')}</Link>
          )}
        </div>
        <p className="surface-muted perf-small">
          {t('reports.intro', 'Build reports across events, tickets, check-in, raffles, auctions, contests, social posts and messages. Save them, export CSV or PDF, or have them sent on a schedule.')}
        </p>
      </section>

      {error && <ErrorState message={error} onRetry={load} />}
      {!error && !data && <LoadingState />}

      {data && data.migrationPending && (
        <div className="note warn" role="status">
          {t('reports.migrationPending', 'Saving, exporting and scheduling reports need a database update. You can still open the system templates.')}
        </div>
      )}
      {noAccess && (
        <div className="note info">{t('reports.noAccess', 'Your role does not have access to reports. Ask your account owner.')}</div>
      )}

      {data && data.templates.length > 0 && (
        <section className="surface" aria-labelledby="rp-tpl-title">
          <h4 id="rp-tpl-title">{t('reports.templates.title', 'System templates')}</h4>
          <ul className="rp-cards">
            {data.templates.map((tpl) => (
              <li key={tpl.key} className="rp-card template">
                <p className="rp-card-title"><FaLock aria-hidden="true" style={{ fontSize: '0.75em', marginRight: 4 }} />{tpl.name}</p>
                <p>{tpl.description}</p>
                <div className="rp-meta">
                  <span className="lm-badge rp-badge-lock">{t('reports.templates.locked', 'Locked')}</span>
                  {tpl.schedules > 0 && <span>{t('reports.scheduledCount', '{{count}} schedule(s)', { count: tpl.schedules })}</span>}
                </div>
                <Link className="lm-btn secondary" to={`/${tenant}/reports/template/${tpl.key}`}>{t('reports.open', 'Open')}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data && !data.migrationPending && (
        <section className="surface" aria-labelledby="rp-saved-title">
          <h4 id="rp-saved-title">{t('reports.saved.title', 'Saved reports')}</h4>
          {data.reports.length === 0 ? (
            <p className="surface-muted perf-small">
              {perms.create ? t('reports.saved.emptyCreate', 'No saved reports yet. Start with “New report”, or duplicate a template.') : t('reports.saved.empty', 'No reports have been shared with you yet.')}
            </p>
          ) : (
            <ul className="rp-cards">
              {data.reports.map((r) => (
                <li key={r.id} className="rp-card">
                  <p className="rp-card-title">{r.name}</p>
                  {r.description && <p>{r.description}</p>}
                  <div className="rp-meta">
                    <span>{r.visibility === 'shared' ? t('reports.saved.shared', 'Shared') : t('reports.saved.private', 'Private')}</span>
                    {!r.mine && r.createdByName && <span>{t('reports.saved.by', 'by {{name}}', { name: r.createdByName })}</span>}
                    {r.schedules > 0 && <span>{t('reports.scheduledCount', '{{count}} schedule(s)', { count: r.schedules })}</span>}
                  </div>
                  <Link className="lm-btn secondary" to={`/${tenant}/reports/${r.id}`}>{t('reports.open', 'Open')}</Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {data && !data.migrationPending && runs.length > 0 && (
        <section className="surface" aria-labelledby="rp-runs-title">
          <h4 id="rp-runs-title">{t('reports.runs.title', 'Recent exports')}</h4>
          <ul className="rp-runs">
            {runs.map((r) => (
              <li key={r.id} className={`rp-run${r.id === highlightRun ? ' highlight' : ''}`}>
                <span>
                  <strong>{r.name}</strong> · {r.format.toUpperCase()} · {when(r.createdOn)}
                  {r.rowCount != null && ` · ${t('reports.export.rows', '{{count}} rows', { count: r.rowCount })}`}
                  {r.truncated && ` · ${t('reports.runs.truncated', 'cut off at the row limit')}`}
                </span>
                <span>
                  {r.status === 'done' && r.downloadUrl
                    ? <a href={runDownloadUrl(r.id)}>{t('reports.export.downloadShort', 'Download')}</a>
                    : <span>{statusLabel[r.status] || r.status}{r.status === 'failed' && r.error ? ` — ${r.error}` : ''}</span>}
                </span>
              </li>
            ))}
          </ul>
          <p className="surface-muted perf-small">{t('reports.runs.ttl', 'Export files are kept for 7 days.')}</p>
        </section>
      )}

      {config && <ReportPermissions config={config} onSaved={load} />}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
          { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}
